import { authFetch } from "@/lib/api-client";

export type EventJobStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
export type EventJobSortBy =
  | "publishedAt"
  | "processingStartedAt"
  | "processingCompletedAt"
  | "runDurationMs"
  | "eventType"
  | "status"
  | "actorUserId"
  | "topic"
  | "correlationId"
  | "consumerInstanceId";

export interface EventJobItem {
  id: string;
  correlationId: string;
  idempotencyKey: string | null;
  topic: string;
  eventType: string;
  payload: Record<string, unknown>;
  attributes: Record<string, unknown>;
  status: EventJobStatus;
  tenantId: string | null;
  tenantName: string | null;
  tenantLogoUrl: string | null;
  tenantBrandColor: string | null;
  actorUserId: string | null;
  actorUserName: string | null;
  actorUserPhotoUrl: string | null;
  consumerInstanceId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  /** Null unless a user interaction produced this event — schedulers have no click. */
  clickedAt: string | null;
  requestReceivedAt: string | null;
  publishedAt: string;
  processingStartedAt: string | null;
  processingCompletedAt: string | null;
  runDurationMs: number | null;
  /** Null for the unsampled majority; a link only exists where this is set. */
  sentryTraceId: string | null;
  createdAt: string;
}

/**
 * One user action: every event sharing a correlation id, collapsed by the API.
 * A click can leave several rows — fan-out, or a Pub/Sub redelivery — so this
 * is the shape that answers "how long did the user wait".
 */
export interface EventActionItem {
  correlationId: string;
  eventType: string;
  topic: string;
  status: EventJobStatus;
  tenantId: string | null;
  tenantName: string | null;
  tenantLogoUrl: string | null;
  tenantBrandColor: string | null;
  actorUserId: string | null;
  actorUserName: string | null;
  actorUserPhotoUrl: string | null;
  clickedAt: string | null;
  requestReceivedAt: string | null;
  publishedAt: string;
  /** Where the clock starts: the click, else ingress, else the publish. */
  startedAt: string;
  lastCompletedAt: string | null;
  /** Null while the action is unfinished — an unfinished wait has no total yet. */
  endToEndMs: number | null;
  jobCount: number;
  failedCount: number;
  sentryTraceId: string | null;
}

export interface EventActionsPage {
  items: EventActionItem[];
  nextCursor: string | null;
  hasMore: boolean;
  totalCount: number | null;
}

/**
 * The event filters minus the two that cannot survive grouping: a status
 * belongs to a row and is derived for the group, and the only order an action
 * has is when it started. Carrying either would put a key in the query cache
 * for a narrowing the endpoint ignores.
 */
export type EventActionsFilters = Omit<EventJobsFilters, "statusIn" | "sortBy">;

export interface EventJobsPage {
  items: EventJobItem[];
  nextCursor: string | null;
  hasMore: boolean;
  /** Total rows matching the filters; only present on the first page. */
  totalCount: number | null;
}

export interface EventJobsFilters {
  statusIn?: EventJobStatus[];
  eventTypeIn?: string[];
  topic?: string;
  topicIn?: string[];
  correlationId?: string;
  actorUserId?: string;
  actorUserIdIn?: string[];
  /** ILIKE contains match against event type, correlation, subject id, or actor. */
  q?: string;
  outcome?: "success" | "failed" | "skipped";
  subjectKind?: string;
  hidePings?: boolean;
  /**
   * Rolling window the server resolves against its own clock, so the filters
   * stay a stable cache key. Omitted means the API's default window.
   */
  windowHours?: number;
  /** ISO instants. Both bounds are inclusive, matching the API's gte/lte. */
  publishedFrom?: string;
  publishedTo?: string;
  sortBy?: EventJobSortBy;
  sortOrder?: "asc" | "desc";
  limit?: number;
}

/** Rolling-window aggregates behind the admin events KPI strip. */
export interface EventJobStats {
  windowHours: number;
  total: number;
  failed: number;
  processingNow: number;
  healthPings: number;
  avgRunMs: number | null;
  p95RunMs: number | null;
  /** p95 of the whole wait — click to completion, queue time included. */
  p95EndToEndMs: number | null;
  /** p95 of publish → pickup: the part of the wait nobody was working. */
  p95QueueWaitMs: number | null;
}

function encodeMulti(values: string[] | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values.join(",");
}

export async function fetchEventJobs(
  filters: EventJobsFilters,
  cursor?: string,
): Promise<EventJobsPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.topic) params.set("topic", filters.topic);
  const topics = encodeMulti(filters.topicIn);
  if (topics) params.set("topic", topics);
  if (filters.correlationId) params.set("correlationId", filters.correlationId);
  if (filters.actorUserId) params.set("actorUserId", filters.actorUserId);
  const actors = encodeMulti(filters.actorUserIdIn);
  if (actors) params.set("actorUserId", actors);
  if (filters.q) params.set("q", filters.q);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.subjectKind) params.set("subjectKind", filters.subjectKind);
  if (filters.hidePings) params.set("hidePings", "true");
  if (filters.windowHours) params.set("windowHours", String(filters.windowHours));
  if (filters.publishedFrom) params.set("publishedFrom", filters.publishedFrom);
  if (filters.publishedTo) params.set("publishedTo", filters.publishedTo);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);

  const statuses = encodeMulti(filters.statusIn);
  if (statuses) params.set("statusIn", statuses);
  const eventTypes = encodeMulti(filters.eventTypeIn);
  if (eventTypes) params.set("eventTypeIn", eventTypes);

  const query = params.toString();
  const { data } = await authFetch<EventJobsPage>(`/admin/events${query ? `?${query}` : ""}`);
  return data;
}

/**
 * The grouped view takes the same filters minus status and sort field: status
 * is derived from the whole group, and the only ordering an action has is when
 * it started.
 */
export async function fetchEventActions(
  filters: EventActionsFilters,
  cursor?: string,
): Promise<EventActionsPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.topic) params.set("topic", filters.topic);
  const actionTopics = encodeMulti(filters.topicIn);
  if (actionTopics) params.set("topic", actionTopics);
  if (filters.correlationId) params.set("correlationId", filters.correlationId);
  if (filters.actorUserId) params.set("actorUserId", filters.actorUserId);
  const actionActors = encodeMulti(filters.actorUserIdIn);
  if (actionActors) params.set("actorUserId", actionActors);
  if (filters.q) params.set("q", filters.q);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.subjectKind) params.set("subjectKind", filters.subjectKind);
  if (filters.hidePings) params.set("hidePings", "true");
  if (filters.windowHours) params.set("windowHours", String(filters.windowHours));
  if (filters.publishedFrom) params.set("publishedFrom", filters.publishedFrom);
  if (filters.publishedTo) params.set("publishedTo", filters.publishedTo);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);

  const eventTypes = encodeMulti(filters.eventTypeIn);
  if (eventTypes) params.set("eventTypeIn", eventTypes);

  const query = params.toString();
  const { data } = await authFetch<EventActionsPage>(
    `/admin/events/actions${query ? `?${query}` : ""}`,
  );
  return data;
}

export interface EventJobUser {
  id: string;
  displayName: string;
  photoUrl: string | null;
}

export async function fetchEventJobUsers(): Promise<EventJobUser[]> {
  const { data } = await authFetch<EventJobUser[]>("/admin/events/users");
  return data;
}

export async function fetchEventJobStats(windowHours?: number): Promise<EventJobStats> {
  const query = windowHours ? `?windowHours=${windowHours}` : "";
  const { data } = await authFetch<EventJobStats>(`/admin/events/stats${query}`);
  return data;
}

export interface EventJobTimeseriesBucket {
  /** ISO start of the bucket; it spans `bucketMinutes` from there. */
  bucketStart: string;
  total: number;
  completed: number;
  failed: number;
  /** PENDING or PROCESSING when the bucket was read — still owed an outcome. */
  inFlight: number;
  avgRunMs: number | null;
  p95RunMs: number | null;
  /** See `EventJobStats.p95EndToEndMs`. Null until something in the bucket finished. */
  p95EndToEndMs: number | null;
  /** See `EventJobStats.p95QueueWaitMs`. Null until something in the bucket was picked up. */
  p95QueueWaitMs: number | null;
}

export interface EventJobTimeseries {
  windowHours: number;
  bucketMinutes: number;
  /** Gapless and ascending: a quiet hour is a zero, never a missing bar. */
  buckets: EventJobTimeseriesBucket[];
}

/**
 * Status is the chart's stacked series, so it is not a filter here — the rest of
 * the panel's filters are, which keeps the chart and the table in agreement.
 */
export interface EventJobTimeseriesFilters {
  eventTypeIn?: string[];
  topic?: string;
  topicIn?: string[];
  actorUserId?: string;
  actorUserIdIn?: string[];
  q?: string;
  outcome?: "success" | "failed" | "skipped";
  subjectKind?: string;
  hidePings?: boolean;
  /** Scopes the window and, with it, the bucket size the server picks. */
  windowHours?: number;
}

export async function fetchEventJobTimeseries(
  filters: EventJobTimeseriesFilters,
): Promise<EventJobTimeseries> {
  const params = new URLSearchParams();
  if (filters.topic) params.set("topic", filters.topic);
  const seriesTopics = encodeMulti(filters.topicIn);
  if (seriesTopics) params.set("topic", seriesTopics);
  if (filters.actorUserId) params.set("actorUserId", filters.actorUserId);
  const seriesActors = encodeMulti(filters.actorUserIdIn);
  if (seriesActors) params.set("actorUserId", seriesActors);
  if (filters.q) params.set("q", filters.q);
  if (filters.outcome) params.set("outcome", filters.outcome);
  if (filters.subjectKind) params.set("subjectKind", filters.subjectKind);
  if (filters.hidePings) params.set("hidePings", "true");
  if (filters.windowHours) params.set("windowHours", String(filters.windowHours));

  const eventTypes = encodeMulti(filters.eventTypeIn);
  if (eventTypes) params.set("eventTypeIn", eventTypes);

  const query = params.toString();
  const { data } = await authFetch<EventJobTimeseries>(
    `/admin/events/timeseries${query ? `?${query}` : ""}`,
  );
  return data;
}

export interface PublishEventInput {
  eventName: string;
  payload: Record<string, unknown>;
  /** Fresh per submission, so a double-click cannot publish twice. */
  idempotencyKey: string;
}

export interface PublishEventResult {
  correlationId: string;
  eventName: string;
  topic: string;
}

export async function publishEvent(input: PublishEventInput): Promise<PublishEventResult> {
  const { data } = await authFetch<PublishEventResult>("/admin/events/publish", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}
